import React, { useState, useEffect } from 'react';
import { Layout } from '../components/Layout';
import { ModuleCard } from '../components/ModuleCard';
import { ModuleViewer } from '../components/ModuleViewer';
import { DriverForm } from '../components/DriverForm';
import { Settings, AlertCircle, Search, Filter, Star, BookOpen, Clock, Award, RotateCcw } from 'lucide-react';
import { modules } from '../lib/api';
import { useCompany } from '../contexts/CompanyContext';
import type { TrainingModule, ModuleResult } from '../types';
import { cn } from '../utils/cn';

export function ModulesPage() {
  const { company } = useCompany();
  const [modulesList, setModulesList] = useState<TrainingModule[]>([]);
  const [selectedModule, setSelectedModule] = useState<TrainingModule | null>(null);
  const [completedModules, setCompletedModules] = useState<string[]>([]);
  const [showDriverForm, setShowDriverForm] = useState(false);
  const [finalScore, setFinalScore] = useState(0);
  const [moduleResults, setModuleResults] = useState<ModuleResult[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [filter, setFilter] = useState<'all' | 'completed' | 'pending'>('all');
  const [resetting, setResetting] = useState(false);

  useEffect(() => {
    loadModules();
    const savedProgress = localStorage.getItem('moduleProgress');
    if (savedProgress) {
      const progress = JSON.parse(savedProgress);
      setCompletedModules(progress.completedModules || []);
      setModuleResults(progress.moduleResults || []);
      setFinalScore(progress.finalScore || 0);
    }
  }, []);

  useEffect(() => {
    const progress = {
      completedModules,
      moduleResults,
      finalScore
    };
    localStorage.setItem('moduleProgress', JSON.stringify(progress));
  }, [completedModules, moduleResults, finalScore]);

  const loadModules = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await modules.list();
      const sortedModules = data?.sort((a, b) => a.order - b.order) || [];
      setModulesList(sortedModules);
    } catch (err: any) {
      console.error('Error loading modules:', err);
      setError(err.message || 'Erro ao carregar módulos. Por favor, tente novamente.');
    } finally {
      setLoading(false);
    }
  };

  const handleModuleComplete = (score: number, moduleResult: ModuleResult) => {
    if (!selectedModule) return;

    console.log('Module completed:', {
      moduleId: selectedModule.id,
      score,
      results: moduleResult.results
    });

    if (score >= 70) {
      // Add to completed modules
      const newCompletedModules = [...completedModules, selectedModule.id];
      setCompletedModules(newCompletedModules);
      
      // Add to module results
      const newModuleResults = [...moduleResults, moduleResult];
      setModuleResults(newModuleResults);

      // Calculate new final score
      const totalScore = newModuleResults.reduce((acc, curr) => acc + curr.score, 0);
      const averageScore = totalScore / newModuleResults.length;
      setFinalScore(averageScore);

      console.log('Progress updated:', {
        completedModules: newCompletedModules.length,
        totalModules: modulesList.length,
        averageScore
      });

      // Check if all required modules are completed
      const requiredModules = modulesList.filter(m => !m.optional);
      const allRequiredCompleted = requiredModules.every(m => 
        newCompletedModules.includes(m.id)
      );

      if (allRequiredCompleted) {
        console.log('All required modules completed, showing driver form');
        setShowDriverForm(true);
      } else {
        // Find next available module
        const nextModule = modulesList.find(m => 
          !newCompletedModules.includes(m.id) && 
          (!m.prerequisites || m.prerequisites.every(p => newCompletedModules.includes(p)))
        );

        if (nextModule) {
          alert(`Parabéns! Você completou o módulo com ${score.toFixed(1)}% de aproveitamento. O próximo módulo "${nextModule.title}" foi desbloqueado.`);
        } else {
          alert(`Parabéns! Você completou o módulo com ${score.toFixed(1)}% de aproveitamento.`);
        }
      }

      setSelectedModule(null);
    } else {
      alert(`Você precisa de pelo menos 70% de aproveitamento para concluir o módulo. Sua pontuação: ${score.toFixed(1)}%`);
    }
  };

  const handleReset = () => {
    if (!confirm('Tem certeza que deseja reiniciar seu progresso? Isso permitirá que você refaça todos os módulos.')) {
      return;
    }

    setResetting(true);
    try {
      // Store completion history in database before clearing
      const historyEntry = {
        completedAt: new Date().toISOString(),
        modules: completedModules,
        results: moduleResults,
        finalScore
      };

      // Clear local storage
      localStorage.removeItem('moduleProgress');

      // Reset state
      setCompletedModules([]);
      setModuleResults([]);
      setFinalScore(0);
      setSelectedModule(null);
      setShowDriverForm(false);

      // Show success message
      alert('Seu progresso foi reiniciado com sucesso! Você pode agora refazer os módulos.');
    } catch (error) {
      console.error('Error resetting progress:', error);
      setError('Não foi possível reiniciar seu progresso. Por favor, tente novamente.');
    } finally {
      setResetting(false);
    }
  };

  const isModuleAvailable = (module: TrainingModule) => {
    if (!module.prerequisites || module.prerequisites.length === 0) return true;
    return module.prerequisites.every(p => completedModules.includes(p));
  };

  const filteredModules = modulesList.filter(module => {
    const matchesSearch = module.title.toLowerCase().includes(search.toLowerCase()) ||
                         module.description.toLowerCase().includes(search.toLowerCase());
    const matchesFilter = filter === 'all' ||
                         (filter === 'completed' && completedModules.includes(module.id)) ||
                         (filter === 'pending' && !completedModules.includes(module.id));
    return matchesSearch && matchesFilter;
  });

  if (showDriverForm) {
    return (
      <Layout hideNavigation>
        <DriverForm 
          score={finalScore} 
          moduleResults={moduleResults}
        />
      </Layout>
    );
  }

  if (selectedModule) {
    return (
      <Layout hideNavigation>
        <ModuleViewer
          module={selectedModule}
          quiz={selectedModule.quiz}
          onComplete={handleModuleComplete}
        />
      </Layout>
    );
  }

  if (loading) {
    return (
      <Layout hideNavigation>
        <div className="flex items-center justify-center min-h-screen">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900" />
        </div>
      </Layout>
    );
  }

  const progress = (completedModules.length / modulesList.length) * 100;

  const stats = [
    {
      name: 'Módulos Completos',
      value: `${completedModules.length} de ${modulesList.length}`,
      icon: BookOpen,
      color: '#2563eb'
    },
    {
      name: 'Média Geral',
      value: finalScore ? `${finalScore.toFixed(1)}%` : 'N/A',
      icon: Star,
      color: '#059669'
    },
    {
      name: 'Tempo Estimado',
      value: `${Math.ceil(modulesList.reduce((acc, m) => acc + m.duration, 0) / 60)}min`,
      icon: Clock,
      color: '#7c3aed'
    }
  ];

  return (
    <Layout hideNavigation>
      <div className="space-y-8 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="flex flex-col items-center text-center space-y-4">
          <img
            src={company.logo}
            alt={company.name}
            className="h-16 w-auto"
          />
          <div>
            <h1 className="text-3xl font-bold text-gray-900">
              Bem-vindo à Plataforma de Treinamento
            </h1>
            <p className="mt-2 text-lg text-gray-600">
              Complete todos os {modulesList.length} módulos para receber sua certificação oficial {company.name}.
            </p>
          </div>
        </div>

        {/* Progress Section with Reset Button */}
        <div className="bg-white rounded-xl shadow-sm p-6">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h3 className="text-lg font-semibold text-gray-900">Seu Progresso</h3>
              <div className="mt-1 text-sm text-gray-500">
                {completedModules.length} de {modulesList.length} módulos concluídos
              </div>
            </div>
            {completedModules.length > 0 && (
              <button
                onClick={handleReset}
                disabled={resetting}
                className={cn(
                  "flex items-center gap-2 px-4 py-2 text-sm font-medium text-gray-700",
                  "rounded-lg border border-gray-300 bg-white",
                  "transition-all hover:bg-gray-50 active:scale-95",
                  "disabled:opacity-50 disabled:cursor-not-allowed"
                )}
              >
                {resetting ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-gray-700" />
                    Reiniciando...
                  </>
                ) : (
                  <>
                    <RotateCcw className="h-4 w-4" />
                    Reiniciar Progresso
                  </>
                )}
              </button>
            )}
          </div>
          <div className="w-full bg-gray-200 rounded-full h-3">
            <div 
              className="h-3 rounded-full transition-all duration-500 bg-[#7c3aed]"
              style={{ width: `${progress}%` }}
            />
          </div>
        </div>

        {/* Stats Grid */}
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
          {stats.map((stat) => (
            <div
              key={stat.name}
              className="relative overflow-hidden rounded-xl bg-white p-6 shadow-sm"
            >
              <dt>
                <div 
                  className="absolute rounded-lg p-3"
                  style={{ backgroundColor: stat.color + '20' }}
                >
                  <stat.icon 
                    className="h-6 w-6"
                    style={{ color: stat.color }}
                  />
                </div>
                <p className="ml-16 truncate text-sm font-medium text-gray-500">
                  {stat.name}
                </p>
              </dt>
              <dd className="ml-16 flex items-baseline">
                <p className="text-2xl font-semibold text-gray-900">{stat.value}</p>
              </dd>
            </div>
          ))}
        </div>

        {error && (
          <div className="rounded-xl bg-red-50 p-4">
            <div className="flex items-center gap-2">
              <AlertCircle className="h-5 w-5 text-red-400" />
              <p className="text-sm text-red-800">{error}</p>
            </div>
          </div>
        )}

        {/* Search and Filter */}
        <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4">
          <div className="relative flex-1 max-w-xs w-full">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <Search className="h-5 w-5 text-gray-400" />
            </div>
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Buscar módulos..."
              className={cn(
                "block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-lg",
                "text-gray-900 placeholder-gray-500 focus:ring-purple-500 focus:border-purple-500"
              )}
            />
          </div>

          <div className="flex items-center gap-2 w-full sm:w-auto">
            <Filter className="h-5 w-5 text-gray-400" />
            <select
              value={filter}
              onChange={(e) => setFilter(e.target.value as any)}
              className={cn(
                "block w-full sm:w-auto rounded-lg border-gray-300",
                "focus:ring-purple-500 focus:border-purple-500"
              )}
            >
              <option value="all">Todos os Módulos</option>
              <option value="completed">Concluídos</option>
              <option value="pending">Pendentes</option>
            </select>
          </div>
        </div>

        {/* Modules Grid */}
        <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
          {filteredModules.map((module) => (
            <ModuleCard
              key={module.id}
              module={module}
              completed={completedModules.includes(module.id)}
              available={isModuleAvailable(module)}
              onClick={() => {
                if (isModuleAvailable(module)) {
                  setSelectedModule(module);
                } else {
                  alert('Complete os módulos anteriores primeiro.');
                }
              }}
            />
          ))}
        </div>

        {filteredModules.length === 0 && (
          <div className="text-center py-12">
            <p className="text-gray-500">
              Nenhum módulo encontrado com os filtros atuais.
            </p>
          </div>
        )}
      </div>
    </Layout>
  );
}